import React, { useEffect, useState } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, FolderKanban, Tag } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const ExpenditureMasters = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('activities'); // 'activities' | 'heads'
  const [activities, setActivities] = useState([]);
  const [heads, setHeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });

  const [deleteDlg, setDeleteDlg] = useState({ open: false, item: null });

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Access denied — Admin only');
      navigate('/expenditures');
      return;
    }
    fetchAll();
    // eslint-disable-next-line
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [a, h] = await Promise.all([
        api.get('/project-activities'),
        api.get('/expenditure-heads'),
      ]);
      setActivities(a.data || []);
      setHeads(h.data || []);
    } catch {
      toast.error('Failed to load masters');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditItem(null);
    setForm({ name: '', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({ name: item.name || '', description: item.description || '' });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    const endpoint = tab === 'activities' ? '/project-activities' : '/expenditure-heads';
    try {
      if (editItem) {
        await api.put(`${endpoint}/${editItem.id}`, form);
        toast.success('Updated successfully');
      } else {
        await api.post(endpoint, form);
        toast.success('Added successfully');
      }
      setDialogOpen(false);
      setEditItem(null);
      setForm({ name: '', description: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const doDelete = async () => {
    const item = deleteDlg.item;
    if (!item) return;
    const endpoint = tab === 'activities' ? '/project-activities' : '/expenditure-heads';
    try {
      await api.delete(`${endpoint}/${item.id}`);
      toast.success('Deleted');
      setDeleteDlg({ open: false, item: null });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
      setDeleteDlg({ open: false, item: null });
    }
  };

  const rows = tab === 'activities' ? activities : heads;

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="expenditure-masters-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Expenditure Masters</h1>
          <p className="text-lg text-slate-600">Manage Project Activities & Expenditure Heads (Admin only)</p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-emerald-700 hover:bg-emerald-800 text-white"
          data-testid="add-master-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add {tab === 'activities' ? 'Project Activity' : 'Expenditure Head'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200">
        <button
          onClick={() => setTab('activities')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            tab === 'activities'
              ? 'border-emerald-700 text-emerald-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-activities"
        >
          <FolderKanban className="w-4 h-4" /> Project Activities ({activities.length})
        </button>
        <button
          onClick={() => setTab('heads')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            tab === 'heads'
              ? 'border-emerald-700 text-emerald-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-heads"
        >
          <Tag className="w-4 h-4" /> Expenditure Heads ({heads.length})
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                    No {tab === 'activities' ? 'project activities' : 'expenditure heads'} added yet
                  </td>
                </tr>
              ) : (
                rows.map((item, idx) => (
                  <tr key={item.id} className="hover:bg-slate-50" data-testid="master-row">
                    <td className="px-4 py-3 text-sm text-slate-500">{idx + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{item.description || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(item)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          data-testid={`edit-master-${item.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteDlg({ open: true, item })}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-master-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setDialogOpen(false); setEditItem(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editItem ? 'Edit' : 'Add'} {tab === 'activities' ? 'Project Activity' : 'Expenditure Head'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="master-name">Name *</Label>
              <Input
                id="master-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="master-name-input"
                placeholder={tab === 'activities' ? 'e.g., Training Programme' : 'e.g., TA/DA'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="master-desc">Description (optional)</Label>
              <Input
                id="master-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="master-desc-input"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-master-button">
                {editItem ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDlg.open}
        onOpenChange={(o) => setDeleteDlg({ open: o, item: o ? deleteDlg.item : null })}
        onConfirm={doDelete}
        title={`Delete ${tab === 'activities' ? 'Project Activity' : 'Expenditure Head'}`}
        description="This action cannot be undone. If any expenditure references this item, deletion will be blocked."
        itemName={deleteDlg.item?.name || ''}
      />
    </div>
  );
};

export default ExpenditureMasters;
